import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ActionDisabledCondition, ActionItemOptions, getRouterShareId, UserAction } from '@mediahaven/shared';
import { ApiFragment, ApplicationConfigService, NavigationLink, RecordType } from '@stylehaven/components';
import { Observable, of } from 'rxjs';
import { MediaHavenActionType } from '../../../entities/action-types.entity';
import { ActionLinkService } from '../../abstract/action-link.service.interface';

@Injectable({ providedIn: 'root' })
export class OpenInMediaPlayerActionService extends ActionLinkService {
    protected action: MediaHavenActionType = MediaHavenActionType.OPEN_IN_EXTENDED_MEDIA_PLAYER;

    // Services
    private readonly configService: ApplicationConfigService = inject(ApplicationConfigService);
    private readonly router: Router = inject(Router);

    // Properties
    private readonly mediaPlayerUrl: string;

    constructor() {
        super();

        const mediaPlayerUrl = this.configService.getInstant('extendedMediaPlayerUrl');

        if (!mediaPlayerUrl) {
            console.warn('OpenInMediaPlayerActionService:: mediaPlayerUrl is not configured');
            return;
        }

        this.mediaPlayerUrl = mediaPlayerUrl;
    }

    /*  Open in Media Player
     *  Available when:
     *   * Is audio- or video(fragment)
     *   * item is NOT deleted
     *   * Item is not a Representation
     *
     *  Disabled when:
     *   * MediaplayerUrl is not set
     *   * Is not completed
     */
    public getUserAction(record: ApiFragment): Observable<UserAction> {
        return of(this.userActionBuilder(record));
    }

    public override userActionBuilder(record: ApiFragment): UserAction {
        const isPlayableMedia: boolean = record.isPlayableMedia();
        const isDeleted: boolean = record.isDeleted();
        const isRepresentation: boolean = record.getMainRecordType() === RecordType.Representation;

        const isMediaPlayerUrlSet = !!this.mediaPlayerUrl;
        const isCompleted: boolean = record.isCompleted();

        const availableConditions: boolean[] = [isPlayableMedia, !isDeleted, !isRepresentation];
        const disableConditions: ActionDisabledCondition[] = [
            {
                condition: !isMediaPlayerUrlSet,
                tooltip: 'open-in-extended-media-player.no-media-player-url',
                entitySpecificTooltip: false
            },
            { condition: !isCompleted, tooltip: 'general.blocked-status' }
        ];

        return new UserAction(availableConditions, disableConditions);
    }

    public getLabelKey(target: ApiFragment): string {
        return 'actions.open-in-extended-media-player';
    }

    protected getLink(record: ApiFragment, actionItemOptions: ActionItemOptions): Observable<NavigationLink> {
        const navigationLink: NavigationLink = {
            route: '',
            target: '_blank'
        };

        const shareId: string | null = getRouterShareId(this.router);

        const urlParts: string[] = [this.mediaPlayerUrl];
        if (!this.mediaPlayerUrl.endsWith('/')) {
            urlParts.push('/');
        }
        urlParts.push('?id=');
        urlParts.push(record.getFragmentId());
        if (shareId) {
            urlParts.push('&shareId=');
            urlParts.push(shareId);
        }

        const externalUrl: string = urlParts.join('');

        return of({
            ...navigationLink,
            route: externalUrl
        });
    }
}
